import {
  DEFAULT_PROMOTIONS_SORT_COLUMN,
  DEFAULT_PROMOTIONS_SORT_ORDER,
} from "@/core/constants/promotion/promoFilterOptions";
import { PromoListItem, getBlankPromotions } from "@/core/entities/promotion/PromoListItem";
import { useWorkspace } from "@/hooks";
import { getSortParamsFromTableSort } from "@/presentation/deduction/utils";
import { Table, TableSortInfoColumnType } from "@ir/ui";
import { Key, useCallback, useEffect, useMemo, useRef } from "react";
import { usePromotionDrawer, usePromotions } from "../hooks";
import { getPromotionSearchKeywords } from "../utils/promotionDisplay.utils";
import { createPromotionsTableColumns } from "../utils/promotionsTableColumns";

interface PromotionsTableProps {
  contentHeight: number;
}

const PromotionsTable = ({ contentHeight }: PromotionsTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { settings } = useWorkspace();
  const { promotions, isLoading, isRefreshing, filterInfo, setFilter, setCurrentPage, getPromotions } =
    usePromotions();
  const { promotionDrawerInfo, setPromotionDrawerInfo, isNextPrevEnabled, setIsNextPrevEnabled } =
    usePromotionDrawer();

  const searchKeywords = useMemo(
    () => getPromotionSearchKeywords(filterInfo.search),
    [filterInfo.search]
  );

  const handleSortChange = useCallback(
    async (sortedData: TableSortInfoColumnType[]) => {
      const nextSort = getSortParamsFromTableSort(sortedData);
      setFilter({
        ...filterInfo,
        sortColumn: nextSort.sortColumn ?? DEFAULT_PROMOTIONS_SORT_COLUMN,
        sortOrder: nextSort.sortOrder ?? DEFAULT_PROMOTIONS_SORT_ORDER,
      });
      setCurrentPage(1);
      await getPromotions({ isLoading: false, isRefreshing: true });
    },
    [filterInfo, setFilter, getPromotions, setCurrentPage]
  );

  const columns = useMemo(
    () =>
      createPromotionsTableColumns({
        isRefreshing,
        searchKeywords,
        dateFormat: settings?.displayDateFormat ?? "MM/dd/yyyy",
        numberFormat: settings?.displayNumberFormat,
      }),
    [isRefreshing, searchKeywords, settings?.displayDateFormat, settings?.displayNumberFormat]
  );

  const blankPromotions = useMemo(() => getBlankPromotions(25), []);

  const dataSource = useMemo(() => {
    if (isRefreshing || (promotions.length === 0 && !isLoading)) {
      return blankPromotions;
    }
    return promotions;
  }, [isRefreshing, blankPromotions, promotions, isLoading]);

  const sortColumn = filterInfo.sortColumn ?? DEFAULT_PROMOTIONS_SORT_COLUMN;
  const sortOrder = filterInfo.sortOrder ?? DEFAULT_PROMOTIONS_SORT_ORDER;

  const handleRowClick = useCallback(
    (_key: Key, record: PromoListItem) => {
      setPromotionDrawerInfo({
        promoEventId: record.promoEventId,
        promoItem: record,
        isPromoDrawerOpen: true,
        nextPrevEnabled: true,
      });
      setIsNextPrevEnabled(true);
    },
    [setPromotionDrawerInfo, setIsNextPrevEnabled]
  );

  useEffect(() => {
    if (promotionDrawerInfo.promoEventId && tableContainerRef.current) {
      const highlightedElementOfTable = document.querySelector(".current-promotion-item") as HTMLElement;
      const containerOfTable = tableContainerRef.current.parentElement;

      if (highlightedElementOfTable && containerOfTable) {
        const rowRect = highlightedElementOfTable.getBoundingClientRect();
        const containerRect = containerOfTable.getBoundingClientRect();
        const stickyHeaderHeight = 50;

        const isAbove = rowRect.top < containerRect.top + stickyHeaderHeight;
        const isBelow = rowRect.bottom > containerRect.bottom - stickyHeaderHeight;

        if (isAbove) {
          containerOfTable.scrollTo({
            top: containerOfTable.scrollTop + (rowRect.top - containerRect.top) - stickyHeaderHeight,
            behavior: "smooth",
          });
        } else if (isBelow) {
          containerOfTable.scrollTo({
            top: containerOfTable.scrollTop + (rowRect.bottom - containerRect.bottom) + stickyHeaderHeight,
            behavior: "smooth",
          });
        }
      }
    }
  }, [promotionDrawerInfo.promoEventId]);

  useEffect(() => {
    if (isNextPrevEnabled) {
      const handleClickOutside = (event: MouseEvent) => {
        if (tableContainerRef.current && !tableContainerRef.current.contains(event.target as Node)) {
          setIsNextPrevEnabled(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isNextPrevEnabled, setIsNextPrevEnabled]);

  return (
    <Table
      ref={tableContainerRef}
      rowHoverable
      sticky
      border="row"
      aria-label="Trade Promotions Table"
      scroll={{ x: "100%", y: contentHeight }}
      columns={columns}
      sortInfo={{
        currentSortInfo: [
          {
            sortColumnKey: sortColumn,
            order: sortOrder,
            field: sortColumn,
          },
        ],
      }}
      onSortChange={handleSortChange}
      dataSource={dataSource}
      onRowClick={isLoading || isRefreshing ? undefined : handleRowClick}
      rowClassName={(_key, record: PromoListItem, index: number) => {
        let className = "table-row-clickable";
        const currentIndex = dataSource.findIndex(item => item.promoEventId === promotionDrawerInfo?.promoEventId);

        if (record.promoEventId === promotionDrawerInfo?.promoEventId) {
          className = className + " current-promotion-item";
        }

        if (isNextPrevEnabled) {
          if (currentIndex === index) {
            className = className + " table-row-focused";
          } else if (currentIndex === index + 1) {
            className = className + " table-row-focused-prev";
          } else if (currentIndex === index - 1) {
            className = className + " table-row-focused-next";
          }
        }
        return className.trim();
      }}
    />
  );
};

export { PromotionsTable };
